import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import { chartPalette } from "@/lib/colors";
import { isRtl } from "@/lib/direction";
import { isDarkTheme } from "@/lib/theme";

function activeChartPalette() {
  return isDarkTheme() ? chartPalette.dark : chartPalette.light;
}

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
);

ChartJS.defaults.font.family = '"IBM Plex Sans Arabic", "Cairo", system-ui, sans-serif';
ChartJS.defaults.color = activeChartPalette().tick;
ChartJS.defaults.plugins.tooltip = {
  ...ChartJS.defaults.plugins.tooltip,
  rtl: isRtl(),
  backgroundColor: activeChartPalette().tooltipBg,
  titleColor: "#fff",
  bodyColor: activeChartPalette().tooltipBody,
  padding: 10,
  cornerRadius: 8,
  titleFont: { family: '"IBM Plex Sans Arabic", "Cairo", sans-serif', weight: 700 },
  bodyFont: { family: '"IBM Plex Sans Arabic", "Cairo", sans-serif' },
};

/**
 * Disable Chart.js entrance animation under `prefers-reduced-motion` or print so
 * the FIRST paint is the final frame. Otherwise rAF-driven animation can leave a
 * blank/empty chart as the rendered state in PDF/print or a background tab.
 * [audit P1-4]
 */
function prefersNoMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return (
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
    window.matchMedia("print").matches
  );
}

// Capture Chart.js's built-in animation default so we can restore it when the
// no-motion preference is turned off (rather than assigning `undefined`, which
// the types reject).
const defaultChartAnimation = ChartJS.defaults.animation;

export function applyChartMotionPreference() {
  ChartJS.defaults.animation = prefersNoMotion() ? false : defaultChartAnimation;
}

applyChartMotionPreference();

// Re-evaluate when the user toggles reduced-motion or enters print.
if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
  window.matchMedia("(prefers-reduced-motion: reduce)").addEventListener?.("change", applyChartMotionPreference);
  window.addEventListener?.("beforeprint", () => {
    ChartJS.defaults.animation = false;
  });
}

export function syncChartDirection() {
  ChartJS.defaults.plugins.tooltip = {
    ...ChartJS.defaults.plugins.tooltip,
    rtl: isRtl(),
  };
}

export function syncChartTheme() {
  const palette = activeChartPalette();
  ChartJS.defaults.color = palette.tick;
  ChartJS.defaults.plugins.tooltip = {
    ...ChartJS.defaults.plugins.tooltip,
    backgroundColor: palette.tooltipBg,
    bodyColor: palette.tooltipBody,
  };
}

export function chartGridColor() {
  return activeChartPalette().grid;
}
